import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useQuery } from "@tanstack/react-query";
import { getOrderDetail } from "api/recovery.api";
import { extractDeclinedCardInfo } from "utils/declinedOrderInfo";

interface Props {
  orderId: string | null;
  onClose: () => void;
}

export default function DeclinedOrderDialog({ orderId, onClose }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["order-detail", orderId],
    queryFn: () => getOrderDetail(orderId!).then((r) => r.data),
    enabled: orderId !== null,
  });

  const order = data?.order;
  const cardInfo = order ? extractDeclinedCardInfo(order) : null;

  return (
    <Dialog open={orderId !== null} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Declined order {orderId}</DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!!error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(error as Error).message || "Failed to load order detail"}
          </Alert>
        )}

        {order && (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Card/pay-source field names haven't been confirmed for this
              CheckoutChamp account yet, so they're not shown as labeled
              fields — the raw order data below has everything needed to
              confirm them.
            </Typography>

            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
              Order status
            </Typography>
            <Typography variant="body2" mb={2}>
              {cardInfo?.orderStatus ?? "-"}
            </Typography>

            {(cardInfo?.last4 || cardInfo?.cardType) && (
              <>
                <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                  Card (best-effort field match)
                </Typography>
                <Typography variant="body2" mb={2}>
                  {[cardInfo.cardType, cardInfo.last4 ? `ending in ${cardInfo.last4}` : null]
                    .filter(Boolean)
                    .join(" ") || "-"}
                </Typography>
              </>
            )}

            {cardInfo?.declineReason && (
              <>
                <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                  Decline reason (best-effort field match)
                </Typography>
                <Typography variant="body2" mb={2}>
                  {cardInfo.declineReason}
                </Typography>
              </>
            )}

            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
              Raw order data
            </Typography>
            <Box
              component="pre"
              sx={{
                bgcolor: "background.default",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 1.5,
                fontSize: "0.75rem",
                maxHeight: 320,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {JSON.stringify(order, null, 2)}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Tooltip title="Not available yet — needs the real CheckoutChamp pay-source field names confirmed from the raw data above">
          <span>
            <Button variant="contained" color="warning" disabled startIcon={<InfoOutlinedIcon />}>
              Charge this card
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
